import React, { MouseEventHandler, useState } from "react";
import { Destination, DestinationType, Organization, ProviderType, SourceWithConnections } from "../common/ObjectTypes";
import { CopyToClipboard, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import SearchChip from "../common/SearchChip";
import { toast } from 'react-toastify';
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import { page_size } from "../../api/apiUtils";
import { useTranslation } from "react-i18next";
import { useTimezone } from "../../context/TimezoneContext";
import { formatSourceDatetime } from "./SourceHelperFunctions";
import { tableClasses, textClasses, containerClasses, paginationClasses, filterClasses, skeletonClasses, buttonClasses } from "../common/tailwindClassnames";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";

type SourcesListProps = {
    sources: SourceWithConnections[],
    totalCount: number,
    take: number,
    offset: number,
    onPageClicked: MouseEventHandler,
    loading: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    globalSearchStr: string,
    onChangeGlobalSearch: (value: string) => void,
    onChangeOwner: Function,
    ownerFilterValue: any,
    onChangeEndpoint: Function,
    endpointFilterValue: any,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    requestSort: Function,
    onViewDetails: Function,
    onViewConnectionDetails: Function,
}

const SourcesList = ({
    sources,
    totalCount,
    take,
    offset,
    onPageClicked,
    loading,
    organizations,
    loadingOrganizations,
    destinationTypes,
    loadingDestinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    providerTypes,
    loadingProviderTypes,
    globalSearchStr,
    onChangeGlobalSearch,
    onChangeOwner,
    ownerFilterValue,
    onChangeEndpoint,
    endpointFilterValue,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    requestSort,
    onViewDetails,
    onViewConnectionDetails,
}: SourcesListProps) => {
    const { t } = useTranslation();
    const { timezone } = useTimezone();
    const [sortDirection, setSortDirection] = useState(true);
    const [sortByColumn, setSortByColumn] = useState("external_id");

    const hasActiveFilters = !!(globalSearchStr || endpointFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSearch('');
        onChangeEndpoint(null);
        onChangeOwner(null);
        onChangeProviderType(null);
        onChangeDestinationType(null);
    };

    return (
        <div className={containerClasses.page}>
            <div className="max-w-list-table mx-auto"><div className="w-full">
            <div className="mb-12 flex justify-between">
                <div>
                    <h1 data-testid="sources_page_header" className={textClasses.heading}>
                        {t("Sources")}
                    </h1>
                </div>
            </div>
            {/* Search row */}
            <div className="flex flex-row items-end gap-3 mb-3">
                <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} placeholder={t("Search")} showClearButton={false} clearOnSearch={true} />
                {globalSearchStr && <SearchChip value={globalSearchStr} onClear={() => onChangeGlobalSearch('')} />}
                {hasActiveFilters && (
                    <button className={`${buttonClasses.resetFilter} h-[38px] mb-2 ml-auto`} onClick={clearAllFilters}>
                        {t("Clear All Filters")}
                    </button>
                )}
            </div>
            {/* Filter dropdowns row */}
            <div className="flex flex-row gap-3 mb-3 flex-nowrap overflow-y-visible">
                {/* Destination */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={endpointFilterValue}
                        onChange={onChangeEndpoint}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-full"
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-full"
                        variant="connectionFilter"
                    />
                </div>
                {/* Provider Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Provider Type")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className="w-full"
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination Type")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-full"
                        variant="connectionFilter"
                    />
                </div>
            </div>
            {/* Table */}
            <div className="flex flex-col w-full">
                <table className={tableClasses.fixedContainer}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={`${tableClasses.headerCell} w-[22%]`}>
                                {t("External ID")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("external_id", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("external_id");
                                }}>
                                    {sortByColumn === "external_id" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={`${tableClasses.headerCell} w-[38%]`}>
                                {t("Connection")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("integration__id", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("integration__id");
                                }}>
                                    {sortByColumn === "integration__id" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={`${tableClasses.headerCell} w-[25%]`}>
                                {t("Destinations")}
                            </th>
                            <th className={`${tableClasses.headerCell} w-[15%]`}>
                                {t("Created At")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("created_at", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("created_at")
                                }}>
                                    {sortByColumn === "created_at" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    {loading ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className={tableClasses.row} data-testid="loading">
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-40 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                                </tr>
                            ))}
                        </tbody>
                            : <tbody className="text-sm">
                                {sources.map((src: any) => {
                                    return (
                                        <tr key={src.source.id}
                                            className={tableClasses.row}>
                                            <td className={tableClasses.cell}>
                                                <span className="block truncate" title={src.source.external_id}>
                                                    {src.source.external_id}
                                                </span>
                                            </td>
                                            <td className={tableClasses.cell}>
                                                <span
                                                    className="block truncate text-blue-basic transition-colors duration-300 cursor-pointer hover:underline"
                                                    title={src.source.provider.name}
                                                    onClick={() => onViewConnectionDetails(src.source.provider.id)}
                                                >
                                                    {src.source.provider.name}
                                                </span>
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {src.source.destinations.map((dest: Destination) => {
                                                    let dest_url: string | URL;
                                                    try {
                                                        dest_url = new URL(dest.base_url).hostname
                                                    } catch (err) {
                                                        dest_url = ""
                                                    }
                                                    return (
                                                        <div key={dest.id} className="flex items-center gap-2 min-w-0">
                                                            <span className="block truncate text-blue-basic transition-colors duration-300 cursor-pointer hover:underline" title={String(dest_url)} onClick={() => onViewDetails(dest.id)}>{dest_url}</span>
                                                            {dest.base_url && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigator.clipboard.writeText(dest.base_url);
                                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                                            position: toast.POSITION.TOP_RIGHT,
                                                                            autoClose: 2000
                                                                        });
                                                                    }}
                                                                    className="flex items-center justify-center flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                                                                    aria-label={t("Copy to clipboard")}
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                <span className="block truncate">{formatSourceDatetime(src.source.created_at, timezone)}</span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>}
                        {!loading && sources.length === 0 &&
                            <tbody><tr><td colSpan={4} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={`${t("You may need to adjust your search or filters to find what you are looking for")}.`}
                                />
                            </td></tr></tbody>}
                </table>
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    initialPage={Math.floor(offset / take)}
                    totalCount={totalCount}
                    take={take}
                    loading={loading}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div></div>
        </div>
    )
};

export default SourcesList;
